import React, { useContext, useState } from 'react';
import { Box, Button, Toolbar as MuiToolbar, SvgIcon, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../constants';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { AddMenuButton, PinAction } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../reducers/user.selectors';
import { useAmplitudeExperiment } from '../../../utils/ampli';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import Breadcrumbs from '../../Breadcrumbs';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import config from 'config';
import searchFiltersToQuery from '../../../utils/searchFiltersToQuery';
import IsReadOnlyChip from './IsReadOnlyChip';
import useScreenSize from '../../../utils/useScreenSize';
import { useReaderContext } from '../../Reader/ReaderContext';
import { Hint } from '../../Hint';
import { noop } from 'lodash';
import ExportDialog from '../../lernsets/ExportDialog';
import { selectProduct } from '../../../reducers/products.selectors';
import AIAssistantToggler from '../AIAssistant/AIAssistantToggler';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';

interface ToolbarProps {
  mindItem?: MindItem
  downloadMindItem?: () => void
  pinActionCallback: (action: PinAction) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ mindItem, downloadMindItem, pinActionCallback }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [isLernsetsDialogOpen, setIsLernsetsDialogOpen] = useState(false);
  const { isSmallScreen, isLargeScreen } = useScreenSize();
  const {
    variant: lernsetVariant,
    isReady: isClientReady,
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.enableLearnsetsFeature);
  const { onboardingStepCounter, onboardingStepCounterMobile } = useReaderContext();
  const isLernsetsActive = isClientReady && lernsetVariant === 'on';
  const showLernsetsCTA =
    mindItem?.fileType === 'document'
    && !mindItem.isReadOnly
    && isLernsetsActive
    && !isSmallScreen
    && user?.hasDocumentLevelAccess;
  const showDownloadButton =
    mindItem?.fileType === 'document'
    && !mindItem.isReadOnly
    && !user?.hideDownloadAction;
  const storageSessionSearchFilters = searchResultsStorageManager.getSearchFilters();

  const { isAssistantAvailable } = useContext(aiAssistantContext);
  const showAIAssistant = mindItem?.fileType === 'document' && isAssistantAvailable && !isLargeScreen;

  const searchPageUrl = `/${routes.search.root}?${searchFiltersToQuery(storageSessionSearchFilters)}`;
  const product = useSelector(state => mindItem && selectProduct(state, mindItem.product));
  const hasProductOverview = product && !!product?.active;

  const getToolbarLeftSide = () => {
    const links = [
      {
        title: t('Search'),
        url: searchPageUrl,
        iconName: 'search' as IconName,
      },
      {
        title: mindItem?.title,
        iconName: 'docText' as IconName,
      },
    ];

    const productPageLink = {
      title: t('Product'),
      url: `/${routes.productPage.root}/${mindItem?.product}`,
      iconName: 'book' as IconName,
    };

    if (hasProductOverview) {
      links.splice(1, 0, productPageLink);
    }

    return <Box data-testid='toolbar__left-side'><Breadcrumbs links={links} /></Box>;
  };

  let toolbarLeftSide = getToolbarLeftSide();

  if (!isLargeScreen) {
    toolbarLeftSide = <Hint
      description={t('Use the overview to quickly return to your search results or to switch to the entire work.')}
      mobilePlacement='bottom-start'
      tabletPlacement='bottom'
      open={onboardingStepCounterMobile?.step === 1}
      onClose={onboardingStepCounterMobile?.dismiss || noop}
      bottomLeftText='1/3'
      actions={{
        primary: {
          label: t('Continue'),
          onClick: onboardingStepCounterMobile?.incrementStep || noop,
          buttonProps: {
            variant: 'text',
            size: 'small',
            sx: {
              color: palette.primary.contrastText,
            }
          }
        }
      }}
      sx={{
        width: 314,
      }}
      useDesktopButtonDesign={true}
    >
      {toolbarLeftSide}
    </Hint>;
  }

  const getDownloadButton = () => {
    if (!isLargeScreen) {
      return (
        <Hint
          open={onboardingStepCounterMobile?.step === 2}
          useDesktopButtonDesign={true}
          description={t('Here you can download the document.')}
          onClose={() => onboardingStepCounterMobile?.dismiss()}
          actions={{
            primary: {
              label: t('Continue'),
              onClick: onboardingStepCounterMobile?.incrementStep || noop,
              buttonProps: {
                variant: 'text',
                size: 'small',
                sx: {
                  color: palette.primary.contrastText,
                }
              }
            }
          }}
          bottomLeftText='2/3'
          placement='bottom-end'
          sx={{
            width: 256,
          }}

        >
          <IconButton
            data-testid='toolbar-download-button'
            onClick={() => {
              downloadMindItem?.();
              if (onboardingStepCounterMobile?.step === 2) onboardingStepCounterMobile?.incrementStep();
            }}
            color='primary'
            size='medium'
          >
            <SvgIcon component={getMuiIcon('download')} />
          </IconButton>
        </Hint>
      );
    }
    return (
      <Button
        variant='contained'
        size='large'
        color='primary'
        data-testid='toolbar-download-button'
        onClick={() => {
          downloadMindItem?.();
          if (onboardingStepCounter?.step === 3) onboardingStepCounter?.dismiss();
        }}
        sx={{
          padding: `${spaces.xs} ${spaces.lg}`,
          whiteSpace: 'nowrap',
        }}
        startIcon={
          <SvgIcon
            component={getMuiIcon('download')}
          />
        }
      >
        {t('Download')}
      </Button>
    );
  };

  return (
    <MuiToolbar
      data-testid='toolbar'
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${palette.elevation.outlined}`,
        backgroundColor: palette.background.paperElevation2,
        padding: `${spaces.sm} ${spaces.md} !important`,
        height: customSizes.readerToolbarHeight,
      }}
    >
      {toolbarLeftSide}
      <Box
        data-testid='toolbar__right-side'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spaces.xs,
        }}
      >
        {
          isLargeScreen &&
          <>
            {
              mindItem &&
              <Hint
                width={326}
                open={onboardingStepCounter?.step === 3}
                placement='bottom'
                description={t('Here you will now find all the options for saving and downloading.')}
                onClose={onboardingStepCounter?.dismiss || noop}
                actions={{
                  primary: {
                    label: t('Close'),
                    onClick: onboardingStepCounter?.dismiss || noop,
                    buttonProps: {
                      variant: 'text',
                      size: 'small',
                      sx: {
                        color: palette.primary.contrastText,
                      }
                    }
                  },
                }}
                bottomLeftText='3/3'
              >
                <Box>
                  <AddMenuButton
                    isPinned={!!mindItem?.isPinned}
                    id={mindItem?.id}
                    itemData={mindItem}
                    type={mindItem?.fileType}
                    buttonSx={{
                      position: 'relative',
                      top: 'unset',
                      right: 0,
                    }}
                    size='medium'
                    pinActionCallback={pinActionCallback}
                    onClick={() => {
                      if (onboardingStepCounter?.step === 3) onboardingStepCounter?.dismiss();
                    }}
                  />
                </Box>
              </Hint>
            }
            {
              showLernsetsCTA &&
              <>
                <ExportDialog
                  open={isLernsetsDialogOpen}
                  onClose={() => setIsLernsetsDialogOpen(false)}
                  mindItemId={mindItem?.id}
                />
                <Button
                  variant='outlined'
                  size='large'
                  color='primary'
                  data-testid='toolbar-reader-lernsets-cta'
                  sx={{
                    padding: `${spaces.xs} ${spaces.lg}`,
                    borderColor: palette.primary.outlinedBorder,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => {
                    setIsLernsetsDialogOpen(true);
                    if (onboardingStepCounter?.step === 3) onboardingStepCounter?.dismiss();
                  }}
                >
                  {t('Edit in Lernsets')}
                </Button>
              </>
            }
          </>
        }
        {
          showDownloadButton && getDownloadButton()
        }
        {
          showAIAssistant && <AIAssistantToggler />
        }
        {
          mindItem?.fileType === 'document' && mindItem?.isReadOnly && <IsReadOnlyChip />
        }
      </Box>
    </MuiToolbar>
  );
};

export default Toolbar;
